import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Generate or retrieve visitor ID (fingerprint-based)
export const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('visitor_id');
  
  if (!visitorId) {
    // Create fingerprint from browser characteristics
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
    ].join('|');
    
    // Generate hash-like ID
    visitorId = btoa(fingerprint).substring(0, 32) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  
  return visitorId;
};

// Get or create session ID (expires after 30 minutes of inactivity)
export const getSessionId = (): string => {
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const now = Date.now();
  
  const storedSession = localStorage.getItem('session_id');
  const storedTimestamp = localStorage.getItem('session_timestamp');
  
  if (storedSession && storedTimestamp) {
    const lastActivity = parseInt(storedTimestamp, 10);
    if (now - lastActivity < SESSION_TIMEOUT) {
      // Update timestamp
      localStorage.setItem('session_timestamp', now.toString());
      return storedSession;
    }
  }
  
  // Create new session
  const newSessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  localStorage.setItem('session_id', newSessionId);
  localStorage.setItem('session_timestamp', now.toString());
  
  return newSessionId;
};

// Detect device type
export const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent;
  
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Capture and persist UTM parameters
export const captureUTMParams = (): Record<string, string> => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
      // Persist to localStorage for cross-page tracking
      localStorage.setItem(key, value);
    } else {
      // Try to retrieve from localStorage
      const stored = localStorage.getItem(key);
      if (stored) {
        utmParams[key] = stored;
      }
    }
  });
  
  return utmParams;
};

// Get persisted UTM params
export const getUTMParams = (): Record<string, string> => {
  const utmParams: Record<string, string> = {};
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  return utmParams;
};

// Track pageview
export const trackPageview = async (page: string) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const utmParams = captureUTMParams();
    const deviceType = getDeviceType();

    // Step 1: Upsert visitor FIRST and wait for it to complete
    const { data: existingVisitor } = await supabase
      .from('analytics_visitors')
      .select('id')
      .eq('visitor_id', visitorId)
      .maybeSingle();

    if (!existingVisitor) {
      await supabase.from('analytics_visitors').insert({
        visitor_id: visitorId,
        first_seen: new Date().toISOString(),
        last_seen: new Date().toISOString(),
        device_type: deviceType,
        utm_source: utmParams.utm_source || null,
        utm_medium: utmParams.utm_medium || null,
        utm_campaign: utmParams.utm_campaign || null,
      });
    } else {
      await supabase
        .from('analytics_visitors')
        .update({ last_seen: new Date().toISOString() })
        .eq('visitor_id', visitorId);
    }

    // Step 2: Upsert session AFTER visitor is confirmed
    const { data: existingSession } = await supabase
      .from('analytics_sessions')
      .select('id')
      .eq('session_id', sessionId)
      .maybeSingle();

    if (!existingSession) {
      await supabase.from('analytics_sessions').insert({
        session_id: sessionId,
        visitor_id: visitorId,
        started_at: new Date().toISOString(),
        device_type: deviceType,
        utm_source: utmParams.utm_source || null,
        utm_medium: utmParams.utm_medium || null,
        utm_campaign: utmParams.utm_campaign || null,
      });
    } else {
      await supabase
        .from('analytics_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('session_id', sessionId);
    }

    // Step 3: Insert pageview — only columns that exist in the table
    await supabase.from('analytics_pageviews').insert({
      session_id: sessionId,
      visitor_id: visitorId,
      page_path: page,
      page_title: document.title || null,
      referrer: document.referrer || null,
      viewed_at: new Date().toISOString(),
    });

  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track opt-in conversion (Step 1)
export const trackOptIn = async (email: string, firstName: string, phone: string) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const utmParams = getUTMParams();

    // Store extra info in metadata since those columns don't exist directly
    await supabase.from('analytics_conversions').insert({
      session_id: sessionId,
      visitor_id: visitorId,
      conversion_type: 'opt_in',
      converted_at: new Date().toISOString(),
      metadata: {
        email,
        first_name: firstName,
        phone,
        utm_source: utmParams.utm_source || null,
        utm_medium: utmParams.utm_medium || null,
        utm_campaign: utmParams.utm_campaign || null,
      },
    });

    // Mark visitor as converted
    await supabase
      .from('analytics_visitors')
      .update({ has_opted_in: true })
      .eq('visitor_id', visitorId);

  } catch (error) {
    console.error('Opt-in tracking error:', error);
  }
};

// Track calendar booking (Step 2)
export const trackBooking = async () => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const utmParams = getUTMParams();

    await supabase.from('analytics_conversions').insert({
      session_id: sessionId,
      visitor_id: visitorId,
      conversion_type: 'booking',
      converted_at: new Date().toISOString(),
      metadata: {
        utm_source: utmParams.utm_source || null,
        utm_medium: utmParams.utm_medium || null,
        utm_campaign: utmParams.utm_campaign || null,
      },
    });

    // Mark visitor as booked
    await supabase
      .from('analytics_visitors')
      .update({ has_booked: true })
      .eq('visitor_id', visitorId);

  } catch (error) {
    console.error('Booking tracking error:', error);
  }
};

// Listen for Calendly booking events
export const initCalendlyTracking = () => {
  window.addEventListener('message', (e) => {
    if (e.origin === 'https://api.leadconnectorhq.com' || e.data.event === 'calendly.event_scheduled') {
      trackBooking();
    }
  });
};